import React, { useEffect, useId, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { get } from '../lib/api';
import { STORE_PAYMENT_OPTIONS, acceptedStorePayments } from '../lib/storePayments';

export default function StoreDetailsTabs({ vendor }) {
  const [tab, setTab] = useState('details');
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState({ key: '', reviews: [], pagination: {}, error: '' });
  const id = useId();
  const tabs = useRef([]);
  const requestKey = `${vendor._id}:${page}:${retry}`;
  const showReviews = vendor.showRatingStatus !== false;
  const loading = result.key !== requestKey;
  useEffect(() => {
    if (tab !== 'reviews' || !showReviews) return undefined;
    const controller = new AbortController();
    get(`/vendors/${vendor._id}/reviews?page=${page}`, { signal: controller.signal })
      .then((response) => {
        if (!response.success) throw new Error(response.message);
        if (!controller.signal.aborted) setResult({ key: requestKey, reviews: response.reviews || [], pagination: response.pagination || {}, error: '' });
      })
      .catch(() => {
        if (!controller.signal.aborted) setResult({ key: requestKey, reviews: [], pagination: {}, error: 'Unable to load reviews. Please try again.' });
      });
    return () => controller.abort();
  }, [tab, vendor._id, page, requestKey, showReviews]);
  const address = vendor.completeAddress || vendor.address?.full || (typeof vendor.address === 'string' ? vendor.address : '');
  const formatTime = (time) => new Date(`2000-01-01T${time}:00`).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const details = [
    ['Status', vendor.isOpen ? 'Open' : 'Closed'],
    ['Address', address],
    ['Payments accepted', STORE_PAYMENT_OPTIONS.filter(option => acceptedStorePayments(vendor.acceptedPaymentMethods).includes(option.value)).map(option => option.label).join(', ')],
    ['Business hours', vendor.businessHours?.open && vendor.businessHours?.close ? `${formatTime(vendor.businessHours.open)} – ${formatTime(vendor.businessHours.close)}` : ''],
    ['Estimated delivery', vendor.storeEtaMinMinutes && vendor.storeEtaMaxMinutes ? `${vendor.storeEtaMinMinutes}–${vendor.storeEtaMaxMinutes} minutes` : ''],
    ['Phone', vendor.contactNumber],
    ['Store status', vendor.showRegistrationDetails ? 'Approved vendor' : ''],
  ].filter(([, value]) => value);
  return (
    <div className="min-w-0 space-y-3">
      <div role="tablist" aria-label="Store information" className="flex border-b">
        {['details', 'reviews'].map((value, index) => (
          <button key={value} ref={(element) => { tabs.current[index] = element; }} type="button" role="tab" id={`${id}-${value}-tab`} aria-controls={`${id}-${value}-panel`} aria-selected={tab === value} tabIndex={tab === value ? 0 : -1}
            onClick={() => setTab(value)}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
              setTab(next === 0 ? 'details' : 'reviews');
              tabs.current[next]?.focus();
            }}
            className={`flex-1 border-b-2 px-3 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${tab === value ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>
            {value === 'details' ? 'Details' : 'Reviews'}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${id}-details-panel`} aria-labelledby={`${id}-details-tab`} hidden={tab !== 'details'}>
        {(vendor.description || vendor.businessDescription) && (
          <p className="mb-3 whitespace-pre-wrap break-words text-sm leading-6 text-gray-600">{vendor.description || vendor.businessDescription}</p>
        )}
        <dl className="divide-y text-sm">
          {details.map(([label, value]) => (
            <div key={label} className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-3">
              <dt className="font-medium text-gray-500">{label}</dt>
              <dd className="min-w-0 break-words text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div role="tabpanel" id={`${id}-reviews-panel`} aria-labelledby={`${id}-reviews-tab`} hidden={tab !== 'reviews'} aria-busy={tab === 'reviews' && showReviews && loading}>
        {!showReviews ? <p className="text-sm text-gray-500">Reviews are not displayed for this store.</p> : loading ? (
          <div role="status" className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500"><span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />Loading reviews...</div>
        ) : result.error ? (
          <div role="alert" className="space-y-3 text-sm"><p className="text-red-600">{result.error}</p><button type="button" onClick={() => setRetry((value) => value + 1)} className="font-semibold text-primary">Try again</button></div>
        ) : (
          <>
            {vendor.stats?.ratingCount > 0 && <p className="mb-3 text-sm text-gray-600">{Number(vendor.stats.ratingAverage || 0).toFixed(1)} out of 5 · {vendor.stats.ratingCount} ratings</p>}
            {result.reviews.length === 0 ? <p className="text-sm text-gray-500">No reviews yet.</p> : (
              <ul className="divide-y">
                {result.reviews.map((review, index) => (
                  <li key={`${page}-${index}`} className="space-y-2 py-3">
                    <p className="break-words text-sm font-semibold text-gray-900">{review.name}</p>
                    <div aria-label={`${review.score} out of 5 stars`} className="flex gap-1">
                      {Array.from({ length: 5 }, (_, star) => <Star key={star} aria-hidden="true" size={16} className={star < review.score ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />)}
                    </div>
                    {review.comment && <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-600">{review.comment}</p>}
                  </li>
                ))}
              </ul>
            )}
            {(page > 1 || result.pagination.hasNext) && <nav aria-label="Review pagination" className="mt-3 flex items-center justify-between gap-3 text-sm"><button type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="text-primary disabled:opacity-40">Previous</button><span>Page {page}</span><button type="button" disabled={!result.pagination.hasNext} onClick={() => setPage((value) => value + 1)} className="text-primary disabled:opacity-40">Next</button></nav>}
          </>
        )}
      </div>
    </div>
  );
}
